import React, {
  useMemo,
} from 'react';

import { ETH } from '../economics/ecoShared';

import type {
  DeclineDataPoint,
} from './declineCalculations';

import {
  fitSepd,
  fitDuong,
  predictSepdRate,
  predictDuongRate,
  integrateForecastRate,
} from './declineCalculations';

export default function AdvancedDeclineModels({
  data,
  forecastEnd,
}: {
  data: DeclineDataPoint[];
  forecastEnd: number;
}) {
  const sepd = useMemo(
    () => fitSepd(data),
    [data],
  );

  const duong = useMemo(
    () => fitDuong(data),
    [data],
  );

  const lastHistoryT =
    data.length > 0
      ? data[
          data.length - 1
        ].t
      : 0;

  const sepdCumulative =
    sepd
      ? integrateForecastRate(
          (t) =>
            predictSepdRate(
              sepd,
              t,
            ),
          forecastEnd,
        )
      : null;

  const duongCumulative =
    duong
      ? integrateForecastRate(
          (t) =>
            predictDuongRate(
              duong,
              t,
            ),
          forecastEnd,
        )
      : null;

  if (data.length < 4) {
    return (
      <div
        style={{
          marginTop: 14,
          padding: 20,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
          color:
            ETH.textMuted,
          textAlign:
            'center',
        }}
      >
        至少需要4个有效生产数据点后进行
        SEPD 与 Duong
        非常规递减分析
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 14,
      }}
    >
      <div
        style={{
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          非常规递减模型
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          SEPD 与 Duong
          模型用于补充评价压裂非常规气井长期瞬态生产特征
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            '1fr 1fr',
          gap: 14,
        }}
      >
        <ModelCard
          title="SEPD"
          equation="q = qi·exp[-(t/τ)ⁿ]"
          ready={Boolean(sepd)}
        >
          <Result
            label="qi"
            value={
              sepd
                ? `${fmt(
                    sepd.qi,
                    3,
                  )} ×10⁴m³/d`
                : '--'
            }
          />

          <Result
            label="τ"
            value={
              sepd
                ? `${fmt(
                    sepd.tau,
                    2,
                  )} d`
                : '--'
            }
          />

          <Result
            label="n"
            value={
              sepd
                ? fmt(
                    sepd.n,
                    3,
                  )
                : '--'
            }
          />

          <Result
            label="R²"
            value={
              sepd
                ? fmt(
                    sepd.r2,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="RMSE"
            value={
              sepd
                ? fmt(
                    sepd.rmse,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="MAE"
            value={
              sepd
                ? fmt(
                    sepd.mae,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="模型积分总累计产气（拟合曲线）"
            value={
              sepdCumulative !==
              null
                ? `${fmt(
                    sepdCumulative,
                    2,
                  )} ×10⁴m³`
                : '--'
            }
            strong
          />
        </ModelCard>

        <ModelCard
          title="Duong"
          equation="q/Gp = a·t⁻ᵐ"
          ready={Boolean(duong)}
        >
          <Result
            label="q1"
            value={
              duong
                ? `${fmt(
                    duong.q1,
                    3,
                  )} ×10⁴m³/d`
                : '--'
            }
          />

          <Result
            label="a"
            value={
              duong
                ? fmt(
                    duong.a,
                    6,
                  )
                : '--'
            }
          />

          <Result
            label="m"
            value={
              duong
                ? fmt(
                    duong.m,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="产量拟合 R²"
            value={
              duong
                ? fmt(
                    duong.r2,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="RMSE"
            value={
              duong
                ? fmt(
                    duong.rmse,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="q/Gp诊断 R²"
            value={
              duong
                ? fmt(
                    duong.diagnosticR2,
                    4,
                  )
                : '--'
            }
          />

          <Result
            label="模型积分总累计产气（拟合曲线）"
            value={
              duongCumulative !==
              null
                ? `${fmt(
                    duongCumulative,
                    2,
                  )} ×10⁴m³`
                : '--'
            }
            strong
          />

          {duong && duong.r2 < 0.9 && (
            <div style={{ marginTop: 10, padding: '8px 10px', border: `1px solid ${ETH.headerBorder}`, background: ETH.canvasBg, fontSize: 11, color: ETH.textSec, lineHeight: 1.6 }}>
              生产历史拟合偏弱（R²={duong.r2.toFixed(4)}）。虽然 q/Gp 诊断线性较好，但该模型对实际产量历史的匹配不足，长期预测结果应谨慎使用。
            </div>
          )}

        </ModelCard>
      </div>

      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          非常规模型生产曲线
        </div>

        <div
          style={{
            marginTop: 4,
            marginBottom: 10,
            fontSize: 11,
            color:
              ETH.textMuted,
          }}
        >
          虚线位置为历史生产结束点；
          其右侧为模型外推预测区
        </div>

        <AdvancedChart
          data={data}
          forecastEnd={
            forecastEnd
          }
          lastHistoryT={
            lastHistoryT
          }
          sepd={sepd}
          duong={duong}
        />
      </div>

      <div
        style={{
          marginTop: 14,
          padding: 14,
          background:
            ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
          fontSize: 11,
          lineHeight: 1.8,
          color:
            ETH.textMuted,
        }}
      >
        <div>
          · SEPD
          用伸展指数描述多个不同衰减时间尺度叠加后的生产递减。
        </div>

        <div>
          · Duong
          模型重点针对压裂井裂缝控制、长期瞬态流生产阶段。
        </div>

        <div>
          · Duong 的
          q/Gp 诊断 R²
          越接近1，说明其基础双对数关系在当前数据段上越接近直线。
        </div>

        <div>
          · 若存在长时间关井、制度调整、水侵或明显生产异常，
          不应直接使用整段生产数据外推。
        </div>
      </div>
    </div>
  );
}

function AdvancedChart({
  data,
  forecastEnd,
  lastHistoryT,
  sepd,
  duong,
}: {
  data: DeclineDataPoint[];
  forecastEnd: number;
  lastHistoryT: number;
  sepd: ReturnType<
    typeof fitSepd
  >;
  duong: ReturnType<
    typeof fitDuong
  >;
}) {
  const width = 900;
  const height = 350;

  const left = 62;
  const right = 30;
  const top = 25;
  const bottom = 48;

  const plotW =
    width -
    left -
    right;

  const plotH =
    height -
    top -
    bottom;

  const maxT =
    Math.max(
      forecastEnd,
      lastHistoryT,
      1,
    );

  const maxDataQ =
    Math.max(
      1,
      ...data.map(
        (point) =>
          point.q,
      ),
    );

  const maxModelQ =
    Math.max(
      sepd?.qi ?? 0,
      duong?.q1 ?? 0,
      maxDataQ,
    );

  const maxQ =
    maxModelQ * 1.08;

  const x = (
    t: number,
  ) =>
    left +
    (t / maxT) *
      plotW;

  const y = (
    q: number,
  ) =>
    top +
    (
      1 -
      q / maxQ
    ) *
      plotH;

  const buildTimes = () => {
    const values: number[] =
      [];

    const step =
      Math.max(
        5,
        maxT / 160,
      );

    for (
      let t = 0;
      t <= maxT;
      t += step
    ) {
      values.push(t);
    }

    values.push(maxT);

    return values;
  };

  const times =
    buildTimes();

  const makePath = (
    fn: (
      t: number,
    ) => number,
  ) =>
    times
      .map(
        (
          t,
          index,
        ) => {
          const q =
            fn(t);

          return `${
            index === 0
              ? 'M'
              : 'L'
          } ${x(t).toFixed(
            2,
          )} ${y(q).toFixed(
            2,
          )}`;
        },
      )
      .join(' ');

  const xTicks =
    Array.from(
      { length: 6 },
      (_, i) =>
        maxT *
        i /
        5,
    );

  const yTicks =
    Array.from(
      { length: 6 },
      (_, i) =>
        maxQ *
        i /
        5,
    );

  return (
    <div
      style={{
        overflowX: 'auto',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          minWidth: 720,
          height,
          display: 'block',
        }}
      >
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill={ETH.bg}
        />

        {yTicks.map(
          (value) => (
            <g key={value}>
              <line
                x1={left}
                x2={
                  width -
                  right
                }
                y1={y(value)}
                y2={y(value)}
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={
                  left - 8
                }
                y={
                  y(value) + 4
                }
                textAnchor="end"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {value.toFixed(
                  1,
                )}
              </text>
            </g>
          ),
        )}

        {xTicks.map(
          (value) => (
            <g key={value}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={top}
                y2={
                  height -
                  bottom
                }
                stroke={
                  ETH.headerBorder
                }
              />

              <text
                x={x(value)}
                y={
                  height -
                  bottom +
                  18
                }
                textAnchor="middle"
                fontSize={10}
                fill={
                  ETH.textMuted
                }
              >
                {Math.round(
                  value,
                )}
              </text>
            </g>
          ),
        )}

        {/* 历史 / 预测分界 */}
        <line
          x1={x(
            lastHistoryT,
          )}
          x2={x(
            lastHistoryT,
          )}
          y1={top}
          y2={
            height -
            bottom
          }
          stroke="#777"
          strokeWidth={1.5}
          strokeDasharray="5 5"
        />

        <text
          x={
            x(
              lastHistoryT,
            ) + 5
          }
          y={top + 15}
          fontSize={9}
          fill={
            ETH.textMuted
          }
        >
          历史/预测分界
        </text>

        {sepd && (
          <path
            d={makePath(
              (t) =>
                predictSepdRate(
                  sepd,
                  t,
                ),
            )}
            fill="none"
            stroke="#b64f35"
            strokeWidth={2.2}
          />
        )}

        {duong && (
          <path
            d={makePath(
              (t) =>
                predictDuongRate(
                  duong,
                  t,
                ),
            )}
            fill="none"
            stroke="#3d6f91"
            strokeWidth={2.2}
            strokeDasharray="7 5"
          />
        )}

        {data.map(
          (
            point,
            index,
          ) => (
            <circle
              key={`${point.date}-${index}`}
              cx={x(
                point.t,
              )}
              cy={y(
                point.q,
              )}
              r={3.8}
              fill={
                ETH.text
              }
            />
          ),
        )}

        <text
          x={
            left +
            plotW / 2
          }
          y={height - 8}
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
        >
          生产时间 t / d
        </text>

        <text
          x={16}
          y={
            top +
            plotH / 2
          }
          textAnchor="middle"
          fontSize={11}
          fill={
            ETH.textSec
          }
          transform={`rotate(-90 16 ${
            top +
            plotH / 2
          })`}
        >
          日产气量 q / 10⁴m³/d
        </text>

        <line
          x1={left + 20}
          x2={left + 50}
          y1={top + 12}
          y2={top + 12}
          stroke="#b64f35"
          strokeWidth={2}
        />

        <text
          x={left + 56}
          y={top + 16}
          fontSize={10}
          fill={
            ETH.textSec
          }
        >
          SEPD
        </text>

        <line
          x1={left + 110}
          x2={left + 140}
          y1={top + 12}
          y2={top + 12}
          stroke="#3d6f91"
          strokeWidth={2}
          strokeDasharray="7 5"
        />

        <text
          x={left + 146}
          y={top + 16}
          fontSize={10}
          fill={
            ETH.textSec
          }
        >
          Duong
        </text>
      </svg>
    </div>
  );
}

function ModelCard({
  title,
  equation,
  ready,
  children,
}: {
  title: string;
  equation: string;
  ready: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 14,
        background:
          ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {title}
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color:
                ETH.textMuted,
              fontFamily:
                ETH.fontMono,
            }}
          >
            {equation}
          </div>
        </div>

        <span
          style={{
            fontSize: 10,
            color: ready
              ? ETH.ok
              : ETH.textMuted,
          }}
        >
          {ready
            ? '已完成拟合'
            : '等待数据'}
        </span>
      </div>

      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gap: 7,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Result({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent:
          'space-between',
        padding: strong
          ? '9px 0'
          : '5px 0',
        borderTop: strong
          ? `1px solid ${ETH.headerBorder}`
          : undefined,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color:
            ETH.textMuted,
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontSize: strong
            ? 15
            : 11,
          fontWeight: strong
            ? 800
            : 600,
          fontFamily:
            ETH.fontMono,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function fmt(
  value: number,
  digits = 4,
) {
  if (
    !Number.isFinite(value)
  ) {
    return '--';
  }

  if (
    Math.abs(value) >
      1e6 ||
    (
      Math.abs(value) >
        0 &&
      Math.abs(value) <
        1e-5
    )
  ) {
    return value.toExponential(
      4,
    );
  }

  return value.toFixed(
    digits,
  );
}